/**
 * GraphQL queries and hooks — tag pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, useApolloClient, type QueryResult } from '@apollo/client/react';
import { useEffect, useState } from 'react';
import type { Tag, TagCategory, TagConnection, TagFilter } from '../types/tag';

export const TAG_FIELDS = gql`
  fragment TagFields on Tag {
    entityId
    entityType
    name
    categoryId
    color
    description
    value
    status
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
  }
`;

export const GET_TAG = gql`
  ${TAG_FIELDS}
  query GetTag($id: ID!) {
    tag(id: $id) {
      ...TagFields
    }
  }
`;

export const LIST_TAGS = gql`
  ${TAG_FIELDS}
  query ListTags($filter: TagFilter, $first: Int, $after: String) {
    tags(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          ...TagFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

// ─── Audit ────────────────────────────────────────────────────────────────────

export const TAGS_BY_ENTITY = gql`
  ${TAG_FIELDS}
  query TagsByEntity($entityId: ID!, $entityType: String!) {
    tagsByEntity(entityId: $entityId, entityType: $entityType) {
      ...TagFields
    }
  }
`;

export function useTag(id: string): QueryResult<{ tag: Tag | null }> {
  return useQuery(GET_TAG, {
    variables: { id },
    skip: !id,
  });
}

export function useTags(
  filter?: TagFilter,
  first: number = 50,
  after?: string,
  skip: boolean = false
): QueryResult<{ tags: TagConnection }> {
  return useQuery(LIST_TAGS, {
    variables: { filter, first, after },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip,
  });
}

export interface UseAllTagsResult {
  tags: Tag[];
  loading: boolean;
  error: Error | undefined;
}

/**
 * Fetch the entire Tag catalog (every page, not capped at the resolver's
 * 100-row-per-request ceiling).
 *
 * Used to resolve tag-category column values for the Projects export (issue
 * #647 — Three Rs Key / Hosting Environment / user-selected tag-group
 * columns), which needs to cross-reference every service's applied tag ids
 * against the full catalog, not just one page of it. Also used by every
 * `TagPicker`-backed "assign tags" surface and by any id→name tag lookup
 * (e.g. `BusinessEnvironmentFactorDetailPage`, `TechnologyCapabilityDetailPage`)
 * — a plain `useTags()` page caps at 50 tags, which both hides catalog
 * entries from the picker's search and can silently drop an already-assigned
 * tag whose id sorts past the first page (issue #1352).
 *
 * Deliberately does **not** reuse the {@link useAllPages} + cache-field-policy
 * pattern used by `useAllServices`/`useCapabilities`/etc: `tags` is also
 * queried with manual `first`/`after` page-by-page navigation on the Tags
 * list route (`routes/tags/index.tsx`), so adding a `keyArgs: ['filter']` +
 * merge policy for the `tags` field would make that page's per-page fetches
 * accumulate into one ever-growing list instead of replacing per page. This
 * hook instead walks pages itself via imperative `client.query` calls with
 * `fetchPolicy: 'no-cache'`, so it never touches the normalized cache entry
 * the Tags list page reads from.
 */
export function useAllTags(pageSize: number = 100): UseAllTagsResult {
  const client = useApolloClient();
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    async function fetchAllTags(): Promise<void> {
      setLoading(true);
      setError(undefined);
      try {
        const collected: Tag[] = [];
        let after: string | undefined;
        let hasNextPage = true;
        while (hasNextPage) {
          const result = await client.query<{ tags: TagConnection }>({
            query: LIST_TAGS,
            variables: { first: pageSize, after },
            fetchPolicy: 'no-cache',
          });
          const connection = result.data?.tags;
          collected.push(...(connection?.edges?.map((edge) => edge.node) ?? []));
          hasNextPage = connection?.pageInfo?.hasNextPage ?? false;
          after = connection?.pageInfo?.endCursor ?? undefined;
          if (!after) hasNextPage = false;
        }
        if (!cancelled) setTags(collected);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchAllTags();
    return () => {
      cancelled = true;
    };
  }, [client, pageSize]);

  return { tags, loading, error };
}

// ─── Tag Categories ─────────────────────────────────────────────────────────

export const TAG_CATEGORY_FIELDS = gql`
  fragment TagCategoryFields on TagCategory {
    entityId
    entityType
    name
    description
    scope
    scopeOwnerId
    color
    icon
    allowMultiple
    applicableEntityTypes
    valueType
    validation {
      min
      max
      regex
    }
    status
    displayOrder
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
  }
`;

export const LIST_TAG_CATEGORIES = gql`
  ${TAG_CATEGORY_FIELDS}
  query ListTagCategories {
    tagCategories {
      ...TagCategoryFields
    }
  }
`;

export function useTagCategories(): QueryResult<{ tagCategories: TagCategory[] }> {
  return useQuery(LIST_TAG_CATEGORIES, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

export function useTagsByEntity(
  entityId: string,
  entityType: string
): QueryResult<{ tagsByEntity: Tag[] }> {
  return useQuery(TAGS_BY_ENTITY, {
    variables: { entityId, entityType },
    skip: !entityId || !entityType,
  });
}

// ─── Information Pillar ────────────────────────────────────────────────────
